import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Eye, FileImage, Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { API } from "./shared";

function Thumb({ fileId, qs, index, active, onPick }) {
  const ref = useRef(null);
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url, stale = false;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      axios.get(`${API}/artwork-preview/${fileId}?page=${index}&thumb=true&${qs}`, { responseType: "blob" })
        .then(r => { if (!stale) { url = URL.createObjectURL(r.data); setSrc(url); } }).catch(() => {});
    }, { rootMargin: "200px" });
    if (ref.current) io.observe(ref.current);
    return () => { stale = true; io.disconnect(); if (url) URL.revokeObjectURL(url); };
  }, [fileId, qs, index]);
  useEffect(() => { if (active) ref.current?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" }); }, [active]);
  return (
    <button ref={ref} data-testid={`artwork-thumb-${index}`} onClick={() => onPick(index)} title={`Page ${index + 1}`}
      style={{ flex: "0 0 auto", width: 64, height: 86, padding: 2, borderRadius: 6, cursor: "pointer", background: "#1E293B",
        border: `2px solid ${active ? "#3B82F6" : "transparent"}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, transition: "border-color .15s" }}>
      {src ? <img src={src} alt="" draggable={false} style={{ maxWidth: "100%", maxHeight: 66, objectFit: "contain" }}/> : <div style={{ width: 44, height: 62, background: "#334155", borderRadius: 3 }}/>}
      <span style={{ fontSize: 10, color: active ? "#93C5FD" : "#94A3B8", fontWeight: 700 }}>{index + 1}</span>
    </button>
  );
}

function Viewer({ asset, qs, onClose }) {
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let url, stale = false;
    setSrc(null); setErr("");
    axios.get(`${API}/artwork-preview/${asset.file_id}?page=${page}&${qs}`, { responseType: "blob" })
      .then(r => { if (stale) return; url = URL.createObjectURL(r.data); setSrc(url); setPages(Number(r.headers["x-page-count"]) || 1); })
      .catch(async e => { if (stale) return; let m = "Preview failed"; try { m = JSON.parse(await e.response.data.text()).detail || m; } catch { /* keep default */ } setErr(m); });
    return () => { stale = true; if (url) URL.revokeObjectURL(url); };
  }, [asset.file_id, page, qs]);
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent data-testid="artwork-preview-modal" style={{ maxWidth: 980, width: "95vw", zIndex: 1300, background: "#fff", padding: 20 }} onContextMenu={e => e.preventDefault()}>
        <DialogTitle style={{ fontSize: 15 }}>{asset.name}</DialogTitle>
        <DialogDescription style={{ fontSize: 12 }}>Watermarked preview for quoting. The original file is released to the winning supplier after payment.</DialogDescription>
        <div style={{ height: "58vh", background: "#0F172A", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
          {err ? <p data-testid="artwork-preview-error" style={{ color: "#FCA5A5", padding: 20 }}>{err}</p>
            : src ? <img data-testid="artwork-preview-image" src={src} alt="Artwork preview" draggable={false} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", userSelect: "none" }}/>
              : <p style={{ color: "#94a3b8" }}>Loading preview…</p>}
        </div>
        {pages > 1 && (
          <div data-testid="artwork-thumb-strip" style={{ display: "flex", gap: 6, overflowX: "auto", padding: 6, background: "#0F172A", borderRadius: 10 }}>
            {Array.from({ length: pages }, (_, i) => <Thumb key={i} fileId={asset.file_id} qs={qs} index={i} active={i === page} onPick={setPage}/>)}
          </div>
        )}
        {pages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <button data-testid="artwork-preview-prev" className="so-btn" disabled={page === 0} onClick={() => setPage(page - 1)}><ChevronLeft size={14}/>Prev</button>
            <span data-testid="artwork-preview-page" style={{ fontSize: 12.5 }}>Page {page + 1} of {pages}</span>
            <button data-testid="artwork-preview-next" className="so-btn" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>Next<ChevronRight size={14}/></button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ArtworkPreviewList({ jobId, rfqId }) {
  const [assets, setAssets] = useState(null);
  const [canDownload, setCanDownload] = useState(false);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const qs = jobId ? `job_id=${jobId}` : `rfq_id=${rfqId}`;
  const save = (blob, name) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 2000);
  };
  const download = async (a) => {
    setBusy(a.file_id);
    try { save((await axios.get(`${API}/files/${a.file_id}`, { responseType: "blob" })).data, a.name); }
    catch { toast.error("Download failed"); } finally { setBusy(null); }
  };
  const downloadAll = async () => {
    setBusy("all");
    try {
      const r = await axios.get(`${API}/jobs/${jobId}/artwork.zip`, { responseType: "blob" });
      const m = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "");
      save(r.data, m ? m[1] : `artwork-${jobId}.zip`);
      toast.success("ZIP downloaded");
    } catch (e) {
      let msg = "";
      try { msg = JSON.parse(await e.response?.data?.text?.())?.detail; } catch { /* non-JSON */ }
      toast.error(msg || `Could not build the ZIP${e.response?.status ? ` (error ${e.response.status})` : ""}`);
    } finally { setBusy(null); }
  };
  useEffect(() => {
    axios.get(`${API}/artwork-preview/list?${qs}`).then(r => { setAssets(r.data.assets); setCanDownload(!!r.data.can_download); }).catch(() => setAssets([]));
  }, [qs]);
  if (!assets || !assets.length) return null;
  return (
    <div data-testid="artwork-preview-list" style={{ border: "1px solid #E2E8F0", background: "#F8FAFC", borderRadius: 12, padding: 14, margin: "14px 0" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 700, fontSize: 13 }}><FileImage size={15}/>Buyer artwork ({assets.length})
        {canDownload && jobId && <button data-testid="artwork-download-all-btn" className="so-btn so-blue" style={{ marginLeft: "auto" }} disabled={busy === "all"} onClick={downloadAll}><Download size={14}/>{busy === "all" ? "Zipping…" : "Download all (ZIP)"}</button>}
      </div>
      <div style={{ fontSize: 12, color: "#64748b", margin: "2px 0 10px", display: "flex", gap: 6, alignItems: "center" }}><Lock size={12}/>{canDownload ? "Payment secured. Original print files are unlocked for download." : "Watermarked preview. Originals unlock for the winner after payment."}</div>
      {assets.map(a => (
        <div key={a.file_id} data-testid={`artwork-preview-row-${a.file_id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid #EEF2F7" }}>
          <span style={{ fontSize: 12.5, wordBreak: "break-all" }}>{a.name}</span>
          <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            {a.previewable
              ? <button data-testid={`artwork-preview-btn-${a.file_id}`} className="so-btn" onClick={() => setOpen(a)}><Eye size={14}/>Preview</button>
              : <span style={{ fontSize: 11.5, color: "#94a3b8", alignSelf: "center" }}>No preview for this file type</span>}
            {canDownload && <button data-testid={`artwork-download-btn-${a.file_id}`} className="so-btn so-blue" disabled={busy === a.file_id} onClick={() => download(a)}><Download size={14}/>{busy === a.file_id ? "Downloading…" : "Download"}</button>}
          </span>
        </div>
      ))}
      {open && <Viewer asset={open} qs={qs} onClose={() => setOpen(null)}/>}
    </div>
  );
}
